import { Injectable } from '@angular/core';
import { LoadingController, LoadingOptions, ToastController, AlertController, AlertOptions } from '@ionic/angular';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { Events } from 'src/providers/events/events';
import { normalizeToastMessage } from 'src/shared/util/util';

@Injectable()
export class CommonUtils {
  constructor(
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private alertController: AlertController,
    private events: Events,
  ) {}

  async showLoading(params?: LoadingOptions): Promise<HTMLIonLoadingElement> {
    const options: LoadingOptions = {
      message: new TranslationPipe().transform('Loading...'),
      spinner: 'circles',
      mode: 'ios',
      ...params,
    };
    const loading = await this.loadingCtrl.create(options);

    loading.present();
    return loading;
  }

  async showInfo(message: string, duration = 5000): Promise<void> {
    const toast = await this.toastCtrl.create({
      message: new TranslationPipe().transform(message),
      duration,
      cssClass: 'toast-info',
    });
    toast.present();
  }

  // `showLogOutPrompt` defaults to false: a "Log out" affordance only makes sense
  // for auth/session errors, not for business/validation errors. Callers handling
  // an auth failure should pass `true` explicitly.
  async showError(message: any, duration = 5000, showLogOutPrompt = false): Promise<void> {
    // Some callers pass a raw error (string / object) rather than a clean string,
    // which rendered the literal "undefined" (or "[object Object]") at the bottom.
    // Normalize to a real message and show nothing when there isn't one.
    const text = normalizeToastMessage(message);
    if (!text) {
      return;
    }
    // Plain-text message (renders reliably, same as the inline tidy-error-message).
    // The logout prompt is a native toast button instead of embedded HTML, which
    // previously relied on an IonicSafeString that wasn't rendering here.
    const toast = await this.toastCtrl.create({
      message: new TranslationPipe().transform(text),
      duration,
      cssClass: 'toast-error',
      buttons: showLogOutPrompt
        ? [{
            text: new TranslationPipe().transform('Log out'),
            icon: 'log-out-outline',
            side: 'end',
            role: 'logout',
            handler: () => { this.logOut(); },
          }]
        : undefined,
    });
    await toast.present();
  }

  async showSuccess(message: string, duration = 5000): Promise<void> {
    const toast = await this.toastCtrl.create({
      message: new TranslationPipe().transform(message),
      duration,
      cssClass: 'toast-success',
    });
    toast.present();
  }

  async showConfirmAlert(options: AlertOptions): Promise<HTMLIonAlertElement> {
    const alert = await this.alertController.create({
      header: new TranslationPipe().transform('Confirm'),
      message: new TranslationPipe().transform('Are you sure you want to do this?'),
      mode: 'ios',
      buttons: [
        {
          text: new TranslationPipe().transform('Go Back'),
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: new TranslationPipe().transform('Confirm'),
          role: 'confirm',
        },
      ],
      ...options,
    });
    alert.present();
    return alert;
  }

  async logOut(): Promise<void> {
    this.events.publish('auth:logout');
  }

  formatGuestReservationDetails(details: any): string {
    try {
      if (!details) return '';

      const hasSomeDetails = details?.total_guests > 0 || details?.total_adults > 0 || details?.total_children > 0 || details?.total_pets > 0 || details?.total_infants > 0;

      if (!hasSomeDetails) return '';

      const translationPipe = new TranslationPipe();
      const parts: string[] = [];

      if (details?.total_guests > 0) {
        const guestWord = translationPipe.transform(details.total_guests === 1 ? 'guest' : 'guests');
        parts.push(`${details.total_guests} ${guestWord}`);
      }

      const guestDetails: string[] = [];

      if (details?.total_adults > 0) {
        const adultWord = translationPipe.transform(details.total_adults === 1 ? 'adult' : 'adults');
        guestDetails.push(`${details.total_adults} ${adultWord}`);
      }

      if (details?.total_children > 0) {
        const childrenWord = translationPipe.transform('children');
        guestDetails.push(`${details.total_children} ${childrenWord}`);
      }

      if (details?.total_pets > 0) {
        const petWord = translationPipe.transform(details.total_pets === 1 ? 'pet' : 'pets');
        guestDetails.push(`${details.total_pets} ${petWord}`);
      }

      if (details?.total_infants > 0) {
        const infantWord = translationPipe.transform(details.total_infants === 1 ? 'infant' : 'infants');
        guestDetails.push(`${details.total_infants} ${infantWord}`);
      }

      if (guestDetails?.length > 0) {
        parts.push(guestDetails.join(' | '));
      }

      const guestsLabel = translationPipe.transform('Guests');
      return parts?.length > 0 ? `${guestsLabel}: ${parts?.join(' | ')}` : '';
    } catch (err) {
      return '';
    }
  }

  parsePhoneNumber(value: string): string {
    const parsedValue = value.replace(/[^\d+]/g, '').replace('+', '');
    return `+${  parsedValue}`;
  }

}
